import { api } from '@/utils/api';
import {
  IonAlert,
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonPage,
  IonSpinner,
  IonTitle,
  IonToast,
  IonToolbar,
} from '@ionic/react';
import { t } from 'i18next';
import { checkmarkCircle, ellipseOutline } from 'ionicons/icons';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { cn } from '../lib/utils';

interface ClubOption {
  id: string;
  label: string;
  type: string;
  number?: number;
  loft?: number;
}

interface ClubSelection {
  [key: string]: boolean;
}

const EditMyBag: React.FC = () => {
  // No longer need activeSegment state
  const [selection, setSelection] = useState<ClubSelection>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showTooManyClubsAlert, setShowTooManyClubsAlert] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<number>(0);
  const history = useHistory();

  // Club options by category
  const clubOptions: Record<string, ClubOption[]> = {
    woods: [
      { id: 'driver', label: 'Driver', type: 'driver' },
      { id: 'wood-2', label: "2 " + t('wood'), type: 'wood', number: 2 },
      { id: 'wood-3', label: "3 " + t('wood'), type: 'wood', number: 3 },
      { id: 'wood-4', label: "4 " + t('wood'), type: 'wood', number: 4 },
      { id: 'wood-5', label: "5 " + t('wood'), type: 'wood', number: 5 },
      { id: 'wood-6', label: "6 " + t('wood'), type: 'wood', number: 6 },
      { id: 'wood-7', label: "7 " + t('wood'), type: 'wood', number: 7 },
      { id: 'wood-8', label: "8 " + t('wood'), type: 'wood', number: 8 },
      { id: 'wood-9', label: "9 " + t('wood'), type: 'wood', number: 9 },
    ],
    hybrids: [
      { id: 'hybrid-1', label: "1 " + t('hybrid'), type: 'hybrid', number: 1 },
      { id: 'hybrid-2', label: "2 " + t('hybrid'), type: 'hybrid', number: 2 },
      { id: 'hybrid-3', label: "3 " + t('hybrid'), type: 'hybrid', number: 3 },
      { id: 'hybrid-4', label: "4 " + t('hybrid'), type: 'hybrid', number: 4 },
      { id: 'hybrid-5', label: "5 " + t('hybrid'), type: 'hybrid', number: 5 },
      { id: 'hybrid-6', label: "6 " + t('hybrid'), type: 'hybrid', number: 6 },
      { id: 'hybrid-7', label: "7 " + t('hybrid'), type: 'hybrid', number: 7 },
      { id: 'hybrid-8', label: "8 " + t('hybrid'), type: 'hybrid', number: 8 },
      { id: 'hybrid-9', label: "9 " + t('hybrid'), type: 'hybrid', number: 9 },
    ],
    irons: [
      { id: 'iron-1', label: "1 " + t('iron'), type: 'iron', number: 1 },
      { id: 'iron-2', label: "2 " + t('iron'), type: 'iron', number: 2 },
      { id: 'iron-3', label: "3 " + t('iron'), type: 'iron', number: 3 },
      { id: 'iron-4', label: "4 " + t('iron'), type: 'iron', number: 4 },
      { id: 'iron-5', label: "5 " + t('iron'), type: 'iron', number: 5 },
      { id: 'iron-6', label: "6 " + t('iron'), type: 'iron', number: 6 },
      { id: 'iron-7', label: "7 " + t('iron'), type: 'iron', number: 7 },
      { id: 'iron-8', label: "8 " + t('iron'), type: 'iron', number: 8 },
      { id: 'iron-9', label: "9 " + t('iron'), type: 'iron', number: 9 },
    ],
    wedges: [
      { id: 'wedge-pw', label: 'Pitching Wedge', type: 'wedge', loft: 46 },
      { id: 'wedge-sw', label: 'Sand Wedge', type: 'wedge', loft: 54 },
      { id: 'wedge-lw', label: 'Lob Wedge', type: 'wedge', loft: 58 },
      { id: 'wedge-50', label: '50° Wedge', type: 'wedge', loft: 50 },
      { id: 'wedge-52', label: '52° Wedge', type: 'wedge', loft: 52 },
      { id: 'wedge-54', label: '54° Wedge', type: 'wedge', loft: 54 },
      { id: 'wedge-56', label: '56° Wedge', type: 'wedge', loft: 56 },
      { id: 'wedge-58', label: '58° Wedge', type: 'wedge', loft: 58 },
      { id: 'wedge-60', label: '60° Wedge', type: 'wedge', loft: 60 },
    ],
    putters: [
      { id: 'putter', label: 'Putter', type: 'putter' },
    ],
  };

  // Section labels for the club categories
  const sections = [
    { id: 'woods', label: 'Bois' },
    { id: 'hybrids', label: 'Hybrides' },
    { id: 'irons', label: 'Fers' },
    { id: 'wedges', label: 'Wedges' },
    { id: 'putters', label: '' },
  ];

  useEffect(() => {
    const fetchUserBag = async () => {
      try {
        setLoading(true);
        const bagResponse = await api.get('/golf-gear/my-bag');

        if (!bagResponse.data) {
          throw new Error('Failed to fetch clubs');
        }

        const clubs = bagResponse.data;

        // Initialize selection state based on user's existing clubs
        const initialSelection: ClubSelection = {};

        // First set all to false
        Object.values(clubOptions).flat().forEach(option => {
          initialSelection[option.id] = false;
        });

        // Then set the user's clubs to true
        clubs.forEach((club: any) => {
          // Match the club from the API to our option IDs
          let optionId = '';

          if (club.type === 'driver') {
            optionId = 'driver';
          } else if (club.type === 'wood') {
            optionId = `wood-${club.clubNumber}`;
          } else if (club.type === 'hybrid') {
            optionId = `hybrid-${club.clubNumber}`;
          } else if (club.type === 'iron') {
            optionId = `iron-${club.clubNumber}`;
          } else if (club.type === 'wedge') {
            if (club.loft) {
              // Find the closest loft option
              const loftOptions = clubOptions.wedges.filter(w => w.loft);
              const closest = loftOptions.reduce((prev, curr) => {
                return (Math.abs((curr.loft || 0) - (club.loft || 0)) < Math.abs((prev.loft || 0) - (club.loft || 0)))
                  ? curr
                  : prev;
              });
              optionId = closest.id;
            } else if (club.clubNumber) {
              // Try to match by name (PW, SW, etc.)
              const wedgeMap: Record<number, string> = {
                0: 'wedge-pw', // Pitching Wedge
                1: 'wedge-gw', // Gap Wedge
                2: 'wedge-sw', // Sand Wedge
                3: 'wedge-lw', // Lob Wedge
              };
              optionId = wedgeMap[club.clubNumber] || '';
            }
          } else if (club.type === 'putter') {
            optionId = 'putter';
          }

          if (optionId && club.inBag) {
            initialSelection[optionId] = true;
          }

          // Set brand if available
          if (club.brandId) {
            setSelectedBrand(club.brandId);
          }
        });

        setSelection(initialSelection);
      } catch (err) {
        console.error('Error fetching clubs:', err);
        setError('Failed to load your golf bag. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserBag();
  }, []);

  const toggleClub = (id: string) => {
    setSelection(prev => {
      // If trying to select a club
      if (!prev[id]) {
        // Count currently selected clubs
        const selectedCount = Object.values(prev).filter(Boolean).length;

        // Check if adding one more would exceed 14
        if (selectedCount >= 14) {
          setShowTooManyClubsAlert(true);
          return prev; // Don't change selection
        }
      }

      // Toggle the selection
      return {
        ...prev,
        [id]: !prev[id]
      };
    });
  };

  const saveBag = async () => {
    try {
      setSaving(true);

      // Convert selection to the format expected by the API
      const selectedClubs = Object.entries(selection)
        .filter(([_, isSelected]) => isSelected)
        .map(([id]) => {
          // Find the club option details
          const category = Object.keys(clubOptions).find(cat =>
            clubOptions[cat].some(option => option.id === id)
          ) || '';

          const option = clubOptions[category]?.find(opt => opt.id === id);

          if (!option) return null;

          return {
            type: option.type,
            clubNumber: option.number,
            loft: option.loft,
            brandId: selectedBrand > 0 ? selectedBrand : undefined,
            inBag: true
          };
        })
        .filter(Boolean); // Remove any nulls

      const response = await api.post('/golf-gear/update-bag', {
        data: { clubs: selectedClubs },
      });

      if (!response.data.clubs) {
        throw new Error('Failed to update bag');
      }

      setSuccessMessage('Votre sac a été mis à jour avec succès!');

      // Navigate back after a short delay
      setTimeout(() => {
        history.push('/my-bag');
      }, 1500);

    } catch (err) {
      console.error('Error saving bag:', err);
      setError('Failed to update your golf bag. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton text={t('back')} defaultHref="/my-bag" />
          </IonButtons>
          <IonTitle>Modifier le Sac</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={saveBag} disabled={saving}>
              {t('save')}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        {loading ? (
          <div className="flex justify-center items-center h-full">
            <IonSpinner name="circular" />
          </div>
        ) : (
          <>
            <div className="space-y-8 pb-4 max-w-3xl mx-auto">
              {/* Club Selection */}
              <p>{t('select_gear_clubs_bag')}</p>
              {sections.map(section => (
                <div key={section.id} className="mb-6">
                  <h2 className="text-xl font-semibold mb-3">{section.label}</h2>
                  <div className="grid grid-cols-2 gap-3">
                    {clubOptions[section.id]?.map(option => (
                      <div
                        key={option.id}
                        className={cn(
                          "border rounded-lg p-4 flex items-center justify-between cursor-pointer",
                          selection[option.id]
                            ? "border-green-500 bg-green-50 dark:bg-green-900/20"
                            : "border-gray-300 dark:border-gray-700"
                        )}
                        onClick={() => toggleClub(option.id)}
                      >
                        <span className="font-medium">{option.label}</span>
                        {selection[option.id] ? (
                          <IonIcon
                            icon={checkmarkCircle}
                            className="text-green-500 text-xl"
                          />
                        ) : (
                          <IonIcon
                            icon={ellipseOutline}
                            className="text-gray-500 text-xl"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <IonToast
          isOpen={!!error}
          message={error || ''}
          duration={3000}
          onDidDismiss={() => setError(null)}
          position="bottom"
          color="danger"
        />

        <IonToast
          isOpen={!!successMessage}
          message={successMessage || ''}
          duration={2000}
          onDidDismiss={() => setSuccessMessage(null)}
          position="bottom"
          color="success"
        />

        <IonAlert
          isOpen={showTooManyClubsAlert}
          onDidDismiss={() => setShowTooManyClubsAlert(false)}
          header="Maximum 14 clubs"
          message="Selon les règles de golf, vous ne pouvez pas avoir plus de 14 clubs dans votre sac."
          buttons={['OK']}
        />
      </IonContent>
      <IonFooter className="md:hidden">
        <IonButton
          expand="block"
          onClick={saveBag}
          disabled={saving}
          className="w-full"
        >
          {saving ? <IonSpinner name="dots" /> : 'Mettre à jour le sac'}
        </IonButton>
      </IonFooter>
    </IonPage>
  );
};

export default EditMyBag;
